import React from "react";
import { Field } from "redux-form";

import { ReduxControls } from "rhombus-react";

import { formatBytes, noop } from "utils/common";
import { ASSET_TYPES, SOCIAL_AD_TYPES } from "utils/constants";
import { CREATIVE_NAME_MAX_LENGTH } from "widgets/forms/validators/Creatives";
import {
	dynamicMapStringToOption,
	dynamicNormalizeOptionToString, isRequiredAssetField,
	nameWithPlacementPrefix,
	nonDynamicFields
} from "social/utils";

import FacebookDisclaimer from "social/FacebookDisclaimer";
import DynamicAssetSelect from "social/DynamicAssetSelect";
import DynamicInput from "social/DynamicInput";
import DynamicSelect from "social/DynamicSelect";
import { isDynamic } from "social/DynamicCreativeToggle";
import { assetType } from "social/BuildMethodRadio";
import { useSocialFields } from "social/formats/utils";
import { FB_CTA_OPTIONS } from "social/constants/CTA";
import usePlacementDynamicUpdate from "hooks/usePlacementDynamicUpdate";
import { FACEBOOK_VIDEO_CONSTRAINTS } from "social/validators/facebook";
import { imposiumCompositionWithoutProjectName } from "social/ImposiumCompSelector";
import { imposiumProject } from "social/ImposiumProjectSelector";
import { afterEffectsPackage } from "social/AfterEffectsPackageSelector";
import { afterEffectsComposition } from "social/AfterEffectsCompSelector";
import { frameForThumbnail } from "social/FrameForThumbnail";

const { MAX_VIDEO_BYTES, MAX_THUMBNAIL_BYTES } = FACEBOOK_VIDEO_CONSTRAINTS;

const MIN_VIDEO_SEC = 1;
const MAX_VIDEO_MIN = 15;
const VIDEO_ACCEPT = ".mp4, .mov";
const THUMBNAIL_ACCEPT = ".jpg, .jpeg, .png, .gif";
const MAX_LENGTH_LANDING_PAGE = 800;

const FILE_SIZE_REQUIREMENT = { name: "File Size:", description: `${formatBytes(MAX_VIDEO_BYTES)} Maximum` };
const FILE_TYPE_REQUIREMENT = { name: "File Types:", description: VIDEO_ACCEPT };
const FILE_DURATION_REQUIREMENT = { name: "Duration:", description: `Between ${MIN_VIDEO_SEC} sec and ${MAX_VIDEO_MIN} min` };

const FACEBOOK_THUMBNAIL_REQUIREMENTS = [
	{
		name: "File Size:",
		description: `<= ${formatBytes(MAX_THUMBNAIL_BYTES)}`,
	},
	{
		name: "File Types:",
		description: THUMBNAIL_ACCEPT,
	},
];

export default function FacebookVideoMODefault(props){
	const {
		libraryId,
		navigationActions,
		creativeAssetType,
		adType,
	} = props;

	const onChangeDefaultIsDynamic = usePlacementDynamicUpdate(SOCIAL_AD_TYPES.FACEBOOK_VIDEO_MO);

	const baseFields = {
		isDynamic,
		assetType,
		imposiumProject,
		imposiumComposition: imposiumCompositionWithoutProjectName,
		afterEffectsPackage,
		afterEffectsComposition,
		video: {
			name: "video",
			label: "Video",
			className: "full-width",
			assetType: ASSET_TYPES.VIDEO,
			component: DynamicAssetSelect,
			defaultDynVarName: nameWithPlacementPrefix("video"),
			requirements: [
				FILE_SIZE_REQUIREMENT,
				FILE_TYPE_REQUIREMENT,
				FILE_DURATION_REQUIREMENT,
				{ name: "Dimensions:", description: "120x120 pixels or larger required, 1080x1080 or larger recommended" }
			],
			creativeAssetType,
			required: isRequiredAssetField(creativeAssetType),
			accept: VIDEO_ACCEPT,
			tip: {
				direction: "top",
				description: "At least 500x889 for better placement compatibility",
			},
			libraryId,
			navigationActions,
			onChange: onChangeDefaultIsDynamic,
		},
		thumbnail: {
			name: "thumbnail",
			label: "Thumbnail",
			className: "full-width",
			assetType: ASSET_TYPES.IMAGE,
			component: DynamicAssetSelect,
			defaultDynVarName: nameWithPlacementPrefix("video_thumbnail"),
			requirements: FACEBOOK_THUMBNAIL_REQUIREMENTS,
			creativeAssetType,
			accept: THUMBNAIL_ACCEPT,
			libraryId,
			navigationActions,
			onChange: onChangeDefaultIsDynamic,
		},
		frameForThumbnail,
		name: {
			name: "name",
			label: "Creative Name",
			className: "full-width",
			placeholder: "Enter a creative name",
			component: ReduxControls.Input,
			charMax: CREATIVE_NAME_MAX_LENGTH,
			showCharCount: true,
			showPristineErrors: true,
			required: true,
		},
		headline: {
			name: "headline",
			label: "Headline",
			className: "full-width",
			placeholder: "Enter a short headline",
			tip: {
				description: "May get truncated after 40 characters",
				direction: "right",
			},
			component: DynamicInput,
			defaultDynVarName: nameWithPlacementPrefix("headline"),
		},
		primaryText: {
			name: "primaryText",
			label: "Primary Text",
			className: "full-width",
			placeholder: "Tell people what your creative is about",
			tip: {
				description: "May get truncated after 125 characters",
				direction: "right",
			},
			component: DynamicInput,
			type: "textarea",
			defaultDynVarName: nameWithPlacementPrefix("copy"),
		},
		description: {
			name: "description",
			label: "Description",
			className: "full-width",
			placeholder: "Include additional details",
			tip: {
				description: "May get truncated after 30 characters",
				direction: "right",
			},
			component: DynamicInput,
			defaultDynVarName: nameWithPlacementPrefix("description"),
		},
		disclaimer: {
			name: "disclaimerEnabled",
			component: FacebookDisclaimer,
			adType,
		},
		landingPage: {
			name: "landingPage",
			label: "Landing Page",
			className: "full-width",
			placeholder: "https://www.example.com",
			component: DynamicInput,
			defaultDynVarName: nameWithPlacementPrefix("clickTag1"),
			charMax: MAX_LENGTH_LANDING_PAGE,
			required: true,
		},
		displayLink: {
			name: "displayLink",
			label: "Display Link",
			className: "half-width",
			placeholder: "Enter link text to be displayed",
			component: DynamicInput,
			defaultDynVarName: nameWithPlacementPrefix("display_link"),
		},
		cta: {
			name: "cta",
			label: "CTA",
			className: "half-width",
			placeholder: "Enter Call to Action Text",
			component: DynamicSelect,
			defaultDynVarName: nameWithPlacementPrefix("cta"),
			options: FB_CTA_OPTIONS,
			normalize: dynamicNormalizeOptionToString,
			format: dynamicMapStringToOption,
			isClearable: false,
			menuPlacement: "top",
			required: true,
			onMenuScrollToBottom: noop,
			variableNameInputTip: "Edit variable name",
		},
		sponsorPageId: {
			name: "sponsorPageId",
			label: "Sponsor Page ID",
			className: "full-width",
			placeholder: "Enter Sponsor Facebook Page ID",
			component: ReduxControls.Input,
			...nonDynamicFields(),
		},
	};

	const fieldProps = useSocialFields(baseFields, props);

	return (
		<div className="fields">
			{fieldProps.map((properties) => {
				return <Field key={properties.name} {...properties} />;
			})}
		</div>
	);
}

FacebookVideoMODefault.displayName = "FacebookVideoMODefaultPlacement";
